import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/providers/ThemeProvider';
import { AuthRoutes } from '@/routes/auth';
import { useAuth } from '@/contexts/AuthContext';
import Dashboard from "./pages/Index";
import { ThemeSync } from "@/components/ThemeSync";
import { MainLayout } from "./components/layout/MainLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const NotFound = lazy(() => import("./pages/NotFound"));
const Agents = lazy(() => import("./pages/Agents"));
const AgentDetail = lazy(() =>
  import("./pages/AgentDetail").then((module) => ({ default: module.AgentDetail }))
);
const Reports = lazy(() => import("./pages/Reports"));
const Calls = lazy(() => import("./pages/Calls"));
const CallDetail = lazy(() => import("./pages/CallDetail"));
const Evaluations = lazy(() => import("./pages/Evaluations"));
const UserSettings = lazy(() => import("./pages/UserSettings"));
const ProjectSettings = lazy(() => import("./pages/ProjectSettings"));
const DataManagement = lazy(() => import("./pages/DataManagement"));
const Assistant = lazy(() => import("./pages/Assistant"));
const Help = lazy(() => import("./pages/Help"));
const Procedures = lazy(() => import("./pages/Procedures"));
const OnboardingWizard = lazy(() => import("./pages/OnboardingWizard"));
const Landing = lazy(() => import("./pages/Landing"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Supprime l'avertissement React DevTools en production
if (typeof window !== 'undefined' && import.meta.env.PROD) {
  const originalConsoleLog = console.log;
  console.log = (...args) => {
    const message = args.join(' ');
    if (message.includes('Download the React DevTools')) {
      return;
    }
    originalConsoleLog(...args);
  };
}

// Composant qui protège les routes nécessitant une authentification
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return <div>Chargement...</div>
  }

  if (!user) {
    return <Navigate to="/auth/login" replace />
  }

  return <>{children}</>
}

function RouteFallback() {
  return <div>Chargement...</div>;
}

function HomeRoute() {
  const { user, loading, isOnboarding } = useAuth();

  if (loading) {
    return <RouteFallback />;
  }

  if (!user || isOnboarding) {
    return <Landing />;
  }

  return (
    <MainLayout>
      <Dashboard />
    </MainLayout>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <ThemeSync />
          <BrowserRouter>
            {/* #66 — sans ce filet, toute exception de rendu se traduisait par
                une page blanche (constatée à la place du rapport d'audit). */}
            <ErrorBoundary>
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Routes d'authentification */}
                <Route path="/auth/*" element={<AuthRoutes />} />

                {/* Accueil: landing publique + dashboard si connecté */}
                <Route path="/" element={<HomeRoute />} />

                {/* Routes protégées */}
                <Route path="/agents" element={<ProtectedRoute><MainLayout><Agents /></MainLayout></ProtectedRoute>} />
                <Route path="/agents/:agentId" element={<ProtectedRoute><MainLayout><AgentDetail /></MainLayout></ProtectedRoute>} />
                <Route path="/appels" element={<ProtectedRoute><MainLayout><Calls /></MainLayout></ProtectedRoute>} />
                <Route path="/appels/:callId" element={<ProtectedRoute><MainLayout><CallDetail /></MainLayout></ProtectedRoute>} />
                <Route path="/assistant" element={<ProtectedRoute><MainLayout><Assistant /></MainLayout></ProtectedRoute>} />
                <Route path="/rapports-audit" element={<ProtectedRoute><MainLayout><Reports /></MainLayout></ProtectedRoute>} />
                <Route path="/procedures" element={<ProtectedRoute><MainLayout><Procedures /></MainLayout></ProtectedRoute>} />
                
                <Route path="/evaluations" element={<ProtectedRoute><MainLayout><Evaluations /></MainLayout></ProtectedRoute>} />

                <Route path="/user/settings" element={<ProtectedRoute><MainLayout><UserSettings /></MainLayout></ProtectedRoute>} />
                <Route path="/reglages" element={<ProtectedRoute><MainLayout><DataManagement /></MainLayout></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><MainLayout><ProjectSettings /></MainLayout></ProtectedRoute>} />
                <Route path="/aide" element={<ProtectedRoute><MainLayout><Help /></MainLayout></ProtectedRoute>} />
                <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </ErrorBoundary>
            <Analytics />
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
